<!--
  ReferenceField — reference-kind renderer (record / user / link / …); the
  chrome-free inner GridCell hosts. Targets render as chips; the picker shows
  only when `mode === "editing"`. `modelValue` is the resolved array of target
  ENTITIES (orphans carried as `{ id }`, rendered Unknown-ID); gestures emit the
  new set (scalar/null single-select, array multi) via `update:modelValue`, and
  updateItem decides persistence (scalar id for store refs, row reconcile for
  link/tagging/…).
-->
<template>
  <div v-if="mode === 'inline'" class="reference-field">
    <div class="input chip-input entity-input" :class="`is-${size}`">
      <template v-for="entity in entities" :key="entity.id">
        <component
          :is="registryEntry.element"
          :value="entity"
          readonly
          :show-parent="false"
          :size="size"
        />
        <span v-if="isOutside(entity.id)" class="outside-filter" :title="OUTSIDE_FILTER">
          <font-awesome-icon icon="triangle-exclamation" />
        </span>
      </template>
    </div>
  </div>

  <div v-else class="reference-field">
    <div class="input chip-input entity-input" :class="`is-${size}`">
      <span v-if="showPicker && scopeNote" class="scope-icon" :title="scopeNote">
        <font-awesome-icon icon="filter" />
      </span>
      <template v-for="entity in entities" :key="entity.id">
        <component
          :is="registryEntry.element"
          :value="entity"
          :readonly="mode !== 'editing'"
          :show-parent="false"
          :size="size"
          @remove="removeTarget(entity.id)"
        />
        <span v-if="isOutside(entity.id)" class="outside-filter" :title="OUTSIDE_FILTER">
          <font-awesome-icon icon="triangle-exclamation" />
        </span>
      </template>
      <AutocompleteInput
        v-if="showPicker"
        ref="autocompleteRef"
        :results="searchResults"
        :placeholder="placeholderText"
        :size="size"
        clear-on-select
        @search="handleSearch"
        @select="handleSelect"
        @remove-last="removeLast"
      >
        <template #suggestion="{ result }">
          <component
            :is="registryEntry.element"
            :value="entityById(result[0])"
            readonly
            fixed
            :show-parent="false"
            :size="size"
          />
        </template>
      </AutocompleteInput>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { type AugerField, hasActiveConditions, withAppearance } from "@auger/core/fields"
import AutocompleteInput from "@auger/ui/components/AutocompleteInput.vue"
import { type FieldMode, type FieldSize } from "@auger/ui/fields/useField"
import {
  OUTSIDE_FILTER,
  REFERENCE_SCOPE,
  type ReferenceEntity,
  referenceEntry,
  searchOptions,
} from "./registry"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: AugerField
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

// Chip element + optional picker `search` per reference type; record / link /
// unregistered fall back to the `record` entry.
const registryEntry = computed(() => referenceEntry(props.field.type))

const placeholderText = computed(() => registryEntry.value.placeholder)
const scopeNote = computed(() => REFERENCE_SCOPE.filterNote(props.field))

// Only a filtered field asks which of its links fall outside the filter.
const { isOutside } = hasActiveConditions(props.field.filter)
  ? REFERENCE_SCOPE.linksOutsideFilter(() => props.field)
  : { isOutside: () => false }

// Picker shows while editing; single-select hides it once a target is chosen
// (replace = remove first). Types with no `search` (e.g. `source`) never show it.
const showPicker = computed(
  () =>
    props.mode === "editing" &&
    !!registryEntry.value.search &&
    (props.field.isArray || entities.value.length === 0)
)

// On edit-open, focus the autocomplete. It mounts when `editing` flips true, so
// defer a tick.
const autocompleteRef = ref<{ focus: () => void } | null>(null)
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    nextTick(() => autocompleteRef.value?.focus())
  }
)

// ids arrive as number or string; normalize so dedup/lookup agree.
const keyOf = (id: number | string) => String(id)
const toArray = <T,>(v: T | T[] | null | undefined): T[] =>
  v == null ? [] : Array.isArray(v) ? v : [v]

// The set to render, straight off `modelValue` (GridCell already picked persisted
// vs draft-merged per mode). Every entity carries a name for display; a nameless
// one (orphan) shows as Unknown-ID.
const entities = computed<ReferenceEntity[]>(() => {
  return (toArray(props.modelValue) as ReferenceEntity[]).map((e) =>
    e.name != null ? e : { ...e, name: `Unknown ID: ${e.id}` }
  )
})

function handleSelect([id]: [number | string, string]) {
  if (props.mode !== "editing") return
  if (entities.value.some((e) => keyOf(e.id) === keyOf(id))) return // dedup no-op
  const entity = searchEntities.value.get(id) ?? { id }
  // Multi: append. Single: replace with the pick (one gesture).
  emit("update:modelValue", props.field.isArray ? [...entities.value, entity] : entity)
}

function removeTarget(id: number | string) {
  if (props.mode !== "editing") return
  const next = entities.value.filter((e) => keyOf(e.id) !== keyOf(id))
  emit("update:modelValue", props.field.isArray ? next : (next[0] ?? null))
}

function removeLast() {
  const es = entities.value
  if (es.length) removeTarget(es[es.length - 1]!.id)
}

// Commit / cancel are pure mode transitions (owned by GridCell); interaction
// persists the draft, diffing against stored so a no-change close writes nothing.

const searchResults = ref<[number | string, string][]>([])
const searchEntities = ref(new Map<number | string, ReferenceEntity>())

function handleSearch(q: string) {
  const search = registryEntry.value.search
  if (!q || !search) {
    searchResults.value = []
    return
  }
  search(q, searchOptions(props.field))
    .then((found) => {
      // A picked result is rendered as a chip straight away, so each record row
      // gets its appearance (icon, colour) as a resolved value would have.
      const entities = found.map(withAppearance)
      searchEntities.value = new Map(entities.map((e) => [e.id, e]))
      searchResults.value = entities.map((e) => [e.id, e.name ?? String(e.id)])
    })
    .catch((err) => {
      console.error("ReferenceField: search failed", err)
      searchResults.value = []
    })
}

const entityById = (id: number | string) => searchEntities.value.get(id) ?? { id }

defineExpose({ entities, showPicker, handleSelect, removeTarget, removeLast, searchEntities })
</script>

<style>
/* Lands on the reference-field root in both modes. No `overflow` here on
   purpose: in-row the base `.grid-cell` clips (do NOT add `overflow-y: auto` — it
   forces a permanent scrollbar); the overlay lets chips wrap via
   `.grid-editor > * { overflow: visible }`. */
.reference-field {
  display: flex;
  align-items: start;
  width: 100%;
  height: 100%;
  min-width: 0;
}

/* In-row only: zero the chip row's `--auger-control-height` min-height (sized for
   a full control, too tall for the compact grid row) so it centers cleanly. The
   overlay wrapper isn't a `.grid-cell`, so it keeps its own sizing. */
.grid-cell .reference-field .chip-input {
  min-height: 0;
}

.reference-field .scope-icon,
.reference-input .scope-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 2px;
  color: var(--auger-link);
  font-size: var(--auger-size-7);
}

.reference-field .outside-filter,
.reference-input .outside-filter {
  display: inline-flex;
  align-items: center;
  margin-left: -2px;
  color: var(--auger-warning, var(--auger-orange-dark));
  font-size: var(--auger-size-7);
}
</style>
